// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from 'react-native';

import { useUser } from '@/app/user-context';
import {
  useCourseAnalysis,
  type AnalysisFeature,
  type HoleAnalysis,
} from '@/features/course-trace/useCourseAnalysis';
import { HoleGuideCard, IDENTITY_GUIDE_VIEW, type GuideViewTransform } from '@/features/hole/holeview/HoleGuideCard';
import { buildHoleGuide, type GuideFeature } from '@/features/hole/holeview/holeGuide';
import { HoleMapCanvas, type MapOverlayPolygon } from '@/features/hole/holeview/HoleMapCanvas';
import { COVER_KINDS, OVERLAY_COLORS } from '@/features/hole/holeview/holeShapes';
import {
  MAX_VIEW_ZOOM,
  MIN_VIEW_ZOOM,
  pointInPolygon,
  type LatLon,
  type ViewTransform,
} from '@/features/hole/holeview/tileMath';
import { ZoomControls } from '@/features/hole/holeview/ZoomControls';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

/** How much a tap of + or − changes the zoom by. Same step the in-round hole view uses. */
const ZOOM_STEP = 1.6;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * The bound a fraction-based pan can reach for a given zoom: none at the fit, growing with
 * it. The same formula {@link HoleGuideCard} clamps its own pan to — kept here too, rather
 * than trusting `HoleMapCanvas`'s own (looser, editor-oriented) clamp, so the two views never
 * drift into showing different amounts of "off the edge" ground.
 */
const clampPanFrac = (value: number, zoom: number) => {
  const bound = (zoom - 1) / 2;
  return clamp(value, -bound, bound);
};

/**
 * Admin-only data-analysis view of one course's mapped features.
 *
 * The hole view answers "where do I hit"; this answers "should we trust the data". Every
 * feature is listed with its provenance — OSM import, player-GPS inference, or satellite
 * image analysis (and which imagery provider) — plus how many players have flagged the
 * hole's data as inaccurate, so an admin can see at a glance where the map needs work.
 */
/** The order the summary counts read in: objects first, then the cover classes. */
const SUMMARY_KINDS = ['tee', 'green', 'bunker', 'water', ...COVER_KINDS];

export default function CourseAnalysisScreen() {
  const { t } = useI18n();
  const { courseId, hole } = useLocalSearchParams<{ courseId?: string; hole?: string }>();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = getApiBaseUrl();

  const { holes, loading, failed } = useCourseAnalysis(
    apiBaseUrl,
    isAdmin ? user?.token : null,
    typeof courseId === 'string' ? courseId : null
  );

  // Opened from the hole view, the page scopes to that hole: the admin is standing on it,
  // and its map with the derived boundaries drawn on is the thing they came to check.
  const holeNumber = typeof hole === 'string' ? Number.parseInt(hole, 10) : NaN;
  const scoped = Number.isFinite(holeNumber) ? holes.find((h) => h.holeNumber === holeNumber) : undefined;
  const visibleHoles = Number.isFinite(holeNumber)
    ? scoped
      ? [scoped]
      : [{ holeNumber, features: [], inaccuracyReports: 0 }]
    : holes;

  // Computed once here, not inside `HoleGuideCard`, purely for its `holeBearingDegrees` and
  // `frameCorners` — the two numbers that let the imagery map below turn and scale to match
  // this same drawing exactly, rather than each view guessing its own angle and span. The
  // guide component still does its own equivalent computation to render itself; this is a
  // second, cheap call over the same pure inputs, not a shared cache — simpler than plumbing
  // the guide's internal result out through a prop it was never designed to expose.
  const guideForScoped = useMemo(
    () => (scoped ? buildHoleGuide(guideInputFor(scoped, holes)) : null),
    [scoped, holes]
  );

  // One piece of state drives both the imagery and the guide drawing below it, expressed as
  // a zoom multiplier plus a pan fraction of each view's own fitted frame — resolution- and
  // coordinate-system-independent, which is what lets a satellite mosaic in screen pixels
  // and an SVG drawing in metres move together from a single source of truth.
  const [syncedView, setSyncedView] = useState<GuideViewTransform>(IDENTITY_GUIDE_VIEW);
  useEffect(() => {
    setSyncedView(IDENTITY_GUIDE_VIEW);
  }, [holeNumber]);
  const stepZoom = useCallback((factor: number) => {
    setSyncedView((current) => ({ ...current, zoom: clamp(current.zoom * factor, MIN_VIEW_ZOOM, MAX_VIEW_ZOOM) }));
  }, []);
  const zoomIn = useCallback(() => stepZoom(ZOOM_STEP), [stepZoom]);
  const zoomOut = useCallback(() => stepZoom(1 / ZOOM_STEP), [stepZoom]);
  const resetZoom = useCallback(() => setSyncedView(IDENTITY_GUIDE_VIEW), []);
  const onPan = useCallback((panXFrac: number, panYFrac: number) => {
    setSyncedView((current) => ({ ...current, panXFrac, panYFrac }));
  }, []);

  // Every feature on the course, by id — a shape drawn on this hole's frame can be
  // attributed to a neighbour (the guide draws by geometry, not by the hole number on the
  // row; see `guideInputFor`), so resolving a tap needs the full course, not just `scoped`.
  const featuresById = useMemo(
    () => new Map(holes.flatMap((each) => each.features).map((feature) => [feature.id, feature])),
    [holes]
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const selectedFeature = selectedFeatureId ? (featuresById.get(selectedFeatureId) ?? null) : null;
  useEffect(() => {
    setSelectedFeatureId(null);
  }, [holeNumber]);

  const router = useRouter();
  const close = () => {
    // Opened from the hole view this pops back to it; on a direct URL there is no
    // history to pop, so land on the start screen instead of doing nothing.
    if (router.canGoBack()) router.back();
    else router.replace('/');
  };

  return (
    <>
      {/* Presented modally: this is a check-and-dismiss overlay on the round, not a
          destination. The header is dropped so the card owns its own chrome, like the
          hole view it opens from; the pinned Close below is the way out. */}
      <Stack.Screen
        options={{ title: t('courseAnalysis.title'), presentation: 'modal', headerShown: false }}
      />
      <View style={styles.screen}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>{t('courseAnalysis.title')}</Text>
        </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        {!isAdmin ? (
          <Text style={styles.notice} testID="course-analysis-forbidden">
            {t('courseAnalysis.adminOnly')}
          </Text>
        ) : loading ? (
          <View style={styles.centered} testID="course-analysis-loading">
            <ActivityIndicator color={appColors.accent.primary} />
          </View>
        ) : failed ? (
          <Text style={styles.notice}>{t('courseAnalysis.failed')}</Text>
        ) : holes.length === 0 ? (
          <Text style={styles.notice} testID="course-analysis-empty">
            {t('courseAnalysis.empty')}
          </Text>
        ) : (
          <>
            {scoped ? (
              <HoleFeatureMap
                hole={scoped}
                view={syncedView}
                onPan={onPan}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetZoom}
                onFeaturePress={setSelectedFeatureId}
                holeBearing={guideForScoped?.holeBearingDegrees ?? null}
                frameCorners={guideForScoped?.frameCorners ?? null}
              />
            ) : null}
            {/* Course-wide, not hole-scoped: one fairway component routinely spans two
                holes and a tree belt runs the length of a stretch, so the guide is handed
                every polygon and keeps the ones that reach into this hole's frame. */}
            {scoped ? (
              <HoleGuideCard
                {...guideInputFor(scoped, holes)}
                view={syncedView}
                onPan={onPan}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetZoom}
                onShapePress={setSelectedFeatureId}
              />
            ) : null}
            {selectedFeature ? (
              <FeatureDetailsPanel feature={selectedFeature} onClose={() => setSelectedFeatureId(null)} />
            ) : null}
            {!Number.isFinite(holeNumber) ? <Summary holes={holes} /> : null}
            {visibleHoles.map((holeData) => (
              <HoleCard key={holeData.holeNumber} hole={holeData} />
            ))}
          </>
        )}
      </ScrollView>
      <View style={styles.footer}>
        <Pressable
          accessibilityRole="button"
          testID="course-analysis-close"
          style={styles.closeButton}
          onPress={close}
        >
          <Text style={styles.closeText}>{t('menu.close')}</Text>
        </Pressable>
      </View>
      </View>
    </>
  );
}

/**
 * What the course-guide drawing needs: this hole's tees and green, and every ring on the
 * course to pick its cover from.
 */
const guideInputFor = (hole: HoleAnalysis, holes: HoleAnalysis[]) => ({
  holeNumber: hole.holeNumber,
  tees: hole.features
    .filter((feature) => feature.kind === 'tee')
    .sort((a, b) => a.ordinal - b.ordinal)
    .map((feature) => ({ lat: feature.lat, lon: feature.lon, label: feature.name })),
  green: greenOf(hole),
  features: holes.flatMap((each) =>
    each.features
      .filter((feature): feature is AnalysisFeature & { ring: { lat: number; lon: number }[] } =>
        feature.ring !== null
      )
      .map((feature): GuideFeature => ({ id: feature.id, kind: feature.kind, ring: feature.ring }))
  ),
});

/** The most confident green on a hole; the guide measures its arcs from that one. */
const greenOf = (hole: HoleAnalysis) => {
  const green = hole.features
    .filter((feature) => feature.kind === 'green')
    .sort((a, b) => b.confidence - a.confidence)[0];
  return green ? { lat: green.lat, lon: green.lon } : null;
};

/**
 * The height {@link HoleMapCanvas}'s canvas is forced to. Its default sizing (aspect-ratio
 * square, off whatever width the card lands at) would leave the pixel size this component
 * measures from the wrapping view disagreeing with the canvas's own internal layout size —
 * which is exactly the size a fraction-of-the-frame pan has to be measured against to land
 * on the same ground the guide drawing pans to.
 */
const MAP_HEIGHT = 380;

/** The hole's imagery with every stored boundary drawn on: bunkers sand, water blue, green green. */
function HoleFeatureMap({
  hole,
  view = IDENTITY_GUIDE_VIEW,
  onPan,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onFeaturePress,
  holeBearing = null,
  frameCorners = null,
}: {
  hole: HoleAnalysis;
  /** Zoom and pan on top of the fitted framing. Defaults to the fit itself. */
  view?: GuideViewTransform;
  /** Reports the pan a drag has reached, as a fraction of this canvas's own fitted frame. */
  onPan?: (panXFrac: number, panYFrac: number) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetZoom?: () => void;
  /** A tap that landed inside a drawn boundary reports that feature's id. */
  onFeaturePress?: (featureId: string) => void;
  /** The guide drawing's own bearing, so this map turns to match it. */
  holeBearing?: number | null;
  /**
   * The guide drawing's own fitted frame, carried back out to real coordinates — fitting
   * this canvas to it, with no further padding of its own, is what makes the two views land
   * on the same scale instead of two independently-fitted ones.
   */
  frameCorners?: [LatLon, LatLon] | null;
}) {
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
  const onMapLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    const next = { width: Math.round(width), height: Math.round(height) };
    setMapSize((current) =>
      current.width === next.width && current.height === next.height ? current : next
    );
  };

  const handlePan = onPan
    ? (pan: { panX: number; panY: number }) => {
        if (!(mapSize.width > 0) || !(mapSize.height > 0)) return;
        onPan(pan.panX / mapSize.width, pan.panY / mapSize.height);
      }
    : undefined;

  const tees = hole.features
    .filter((feature) => feature.kind === 'tee')
    .sort((a, b) => a.ordinal - b.ordinal)
    .map((feature) => ({ lat: feature.lat, lon: feature.lon, label: feature.name }));
  const green = greenOf(hole);
  const polygons: MapOverlayPolygon[] = hole.features
    // Objects only. This canvas frames itself on every ring vertex it is given, and a
    // cover polygon spans holes — one tree belt attributed here would zoom the imagery
    // out to the whole course. Cover belongs to the guide below, which clips instead.
    .filter((feature) => feature.ring !== null && !COVER_KINDS.includes(feature.kind))
    .map((feature) => ({
      id: feature.id,
      ring: feature.ring as { lat: number; lon: number }[],
      color: OVERLAY_COLORS[feature.kind] ?? '#FFFFFF',
    }));

  // Nothing locatable at all -> no frame to draw.
  if (tees.length === 0 && !green && polygons.length === 0) return null;

  const zoom = clamp(view.zoom, MIN_VIEW_ZOOM, MAX_VIEW_ZOOM);
  const mapView: ViewTransform = {
    zoom,
    panX: clampPanFrac(view.panXFrac, zoom) * mapSize.width,
    panY: clampPanFrac(view.panYFrac, zoom) * mapSize.height,
  };

  // A tap that never moved reaches here as a coordinate, not an id — `HoleMapCanvas` only
  // knows lat/lon, not which boundary (if any) it fell inside, so that has to be worked out
  // here against the same `polygons` the canvas was handed to draw. Topmost first, matching
  // how overlapping shapes read visually: whichever was drawn last is what is on top.
  const onMapPress = onFeaturePress
    ? (point: LatLon) => {
        for (let index = polygons.length - 1; index >= 0; index -= 1) {
          const polygon = polygons[index];
          const ring = polygon.ring.map((vertex) => ({ x: vertex.lon, y: vertex.lat }));
          if (pointInPolygon({ x: point.lon, y: point.lat }, ring)) {
            onFeaturePress(polygon.id);
            return;
          }
        }
      }
    : undefined;

  return (
    <View style={styles.map} onLayout={onMapLayout} testID="course-analysis-map">
      <HoleMapCanvas
        tees={tees}
        green={green}
        loading={false}
        unavailable={false}
        polygons={polygons}
        canvasStyle={styles.mapCanvas}
        view={mapView}
        onPan={handlePan}
        onMapPress={onMapPress}
        holeBearing={holeBearing}
        // The guide's own fitted frame, already padded — fitting to it with zero padding of
        // this canvas's own is what makes the two views land on the same real-world span.
        // `tees`/`green` stay empty in the override since only the two corners matter for
        // the fit; `tees`/`green` above are still what gets *drawn*.
        frame={frameCorners ? { tees: [], green: null, polygons: [{ ring: frameCorners }] } : undefined}
        horizontalPadFraction={frameCorners ? 0 : undefined}
        verticalPadFraction={frameCorners ? 0 : undefined}
        // The guide has no minimum-span floor of its own — its 18 m padding is the only
        // margin a narrow hole gets. Leaving this canvas's own 60 m floor in place widened
        // it past that on any hole whose across-span came in under 60 m (most of them), which
        // is what actually broke the scale match sideways: not the angle, the width.
        minSpanMeters={frameCorners ? 0 : undefined}
      />
      {onZoomIn && onZoomOut && onResetZoom ? (
        <ZoomControls
          zoom={zoom}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onReset={onResetZoom}
          testIDPrefix="course-analysis-map"
        />
      ) : null}
    </View>
  );
}

/** Feature counts by kind across the course, so the imbalance jumps out first. */
function Summary({ holes }: { holes: HoleAnalysis[] }) {
  const { t } = useI18n();
  const counts = new Map<string, number>();
  let reports = 0;
  for (const hole of holes) {
    reports += hole.inaccuracyReports;
    for (const feature of hole.features) {
      counts.set(feature.kind, (counts.get(feature.kind) ?? 0) + 1);
    }
  }

  return (
    <View style={styles.summary} testID="course-analysis-summary">
      {SUMMARY_KINDS.map((kind) => (
        <View key={kind} style={styles.summaryItem}>
          <Text style={styles.summaryCount}>{counts.get(kind) ?? 0}</Text>
          <Text style={styles.summaryLabel}>{t(`courseAnalysis.kind.${kind}`)}</Text>
        </View>
      ))}
      <View style={styles.summaryItem}>
        <Text style={[styles.summaryCount, reports > 0 && styles.reportCount]}>{reports}</Text>
        <Text style={styles.summaryLabel}>{t('courseAnalysis.reports')}</Text>
      </View>
    </View>
  );
}

function HoleCard({ hole }: { hole: HoleAnalysis }) {
  const { t } = useI18n();
  return (
    <View style={styles.card} testID={`course-analysis-hole-${hole.holeNumber}`}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>
          {hole.holeNumber === 0
            ? t('courseAnalysis.unattributed')
            : t('courseAnalysis.holeTitle', { hole: hole.holeNumber })}
        </Text>
        {hole.inaccuracyReports > 0 ? (
          <View style={styles.reportBadge} testID={`course-analysis-reports-${hole.holeNumber}`}>
            <Text style={styles.reportBadgeText}>
              {t('courseAnalysis.reportBadge', { count: hole.inaccuracyReports })}
            </Text>
          </View>
        ) : null}
      </View>

      {hole.features.length === 0 ? (
        <Text style={styles.noFeatures}>{t('courseAnalysis.noFeatures')}</Text>
      ) : (
        hole.features.map((feature) => <FeatureRow key={feature.id} feature={feature} />)
      )}
    </View>
  );
}

function FeatureRow({ feature }: { feature: AnalysisFeature }) {
  const { t } = useI18n();
  const kindKey = SUMMARY_KINDS.includes(feature.kind) ? feature.kind : 'other';

  return (
    <View style={styles.row} testID={`course-analysis-row-${feature.id}`}>
      <View style={styles.rowTop}>
        <Text style={styles.rowKind}>
          {t(`courseAnalysis.kind.${kindKey}`)}
          {feature.name ? ` ${feature.name}` : ''}
        </Text>
        <View style={[styles.sourceBadge, badgeStyle(feature.source)]}>
          <Text style={styles.sourceBadgeText}>{sourceLabelFor(feature, t)}</Text>
        </View>
      </View>
      <Text style={styles.rowMeta}>
        {t('courseAnalysis.meta', {
          confidence: Math.round(feature.confidence * 100),
          state: t(feature.isReviewed ? 'courseAnalysis.reviewed' : 'courseAnalysis.unreviewed'),
        })}
        {feature.hasPolygon ? ` · ${t('courseAnalysis.polygon')}` : ''}
      </Text>
    </View>
  );
}

/**
 * Full detail on one tapped feature — the same provenance {@link FeatureRow} lists further
 * down the page, surfaced right where the tap landed instead of making an admin scroll to
 * find the matching row among however many the hole has.
 */
function FeatureDetailsPanel({
  feature,
  onClose,
}: {
  feature: AnalysisFeature;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const kindKey = SUMMARY_KINDS.includes(feature.kind) ? feature.kind : 'other';

  return (
    <View style={styles.detailsPanel} testID={`course-analysis-details-${feature.id}`}>
      <View style={styles.detailsHeader}>
        <Text style={styles.detailsTitle}>
          {t(`courseAnalysis.kind.${kindKey}`)}
          {feature.name ? ` ${feature.name}` : ''}
        </Text>
        <Pressable
          accessibilityRole="button"
          testID="course-analysis-details-close"
          onPress={onClose}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.detailsCloseText}>{t('menu.close')}</Text>
        </Pressable>
      </View>

      <View style={[styles.sourceBadge, badgeStyle(feature.source), styles.detailsSourceBadge]}>
        <Text style={styles.sourceBadgeText}>{sourceLabelFor(feature, t)}</Text>
      </View>

      <Text style={styles.detailsMeta}>
        {t('courseAnalysis.meta', {
          confidence: Math.round(feature.confidence * 100),
          state: t(feature.isReviewed ? 'courseAnalysis.reviewed' : 'courseAnalysis.unreviewed'),
        })}
        {feature.hasPolygon ? ` · ${t('courseAnalysis.polygon')}` : ` · ${t('courseAnalysis.centreOnly')}`}
      </Text>

      <Text style={styles.detailsMeta}>
        {t('courseAnalysis.holeTitle', { hole: feature.holeNumber })}
        {' · '}
        {t('courseAnalysis.sampleCount', { count: feature.sampleCount })}
      </Text>
    </View>
  );
}

const sourceLabelFor = (
  feature: AnalysisFeature,
  t: (key: string, params?: Record<string, string | number>) => string
) =>
  feature.source === 'satellite' && feature.sourceProvider
    ? `${t('courseAnalysis.source.satellite')} · ${feature.sourceProvider}`
    : ['osm', 'gps', 'lantmateriet', 'satellite'].includes(feature.source)
      ? t(`courseAnalysis.source.${feature.source}`)
      : feature.source;

const badgeStyle = (source: string) => {
  if (source === 'osm') return styles.badgeOsm;
  if (source === 'lantmateriet') return styles.badgeLantmateriet;
  if (source === 'satellite') return styles.badgeSatellite;
  return styles.badgeGps;
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: appColors.surface.base },
  scroll: { flex: 1 },
  content: { padding: 16, gap: 12 },
  modalHeader: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
  },
  modalTitle: { color: appColors.text.primary, fontSize: 20, fontWeight: '700' },
  footer: {
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: appColors.border.strong,
  },
  closeButton: {
    alignSelf: 'stretch',
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeText: {
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  map: {
    height: MAP_HEIGHT,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    overflow: 'hidden',
  },
  mapCanvas: { height: MAP_HEIGHT },
  centered: { paddingVertical: 48, alignItems: 'center' },
  notice: {
    color: appColors.text.secondary,
    textAlign: 'center',
    paddingVertical: 48,
    fontSize: 14,
  },
  summary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: appColors.surface.alt,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 12,
  },
  summaryItem: { alignItems: 'center', flex: 1 },
  summaryCount: { color: appColors.text.primary, fontSize: 18, fontWeight: '700' },
  summaryLabel: { color: appColors.text.secondary, fontSize: 11, marginTop: 2 },
  reportCount: { color: appColors.status.warning },
  card: {
    backgroundColor: appColors.surface.alt,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardTitle: { color: appColors.text.primary, fontSize: 16, fontWeight: '700' },
  reportBadge: {
    backgroundColor: '#7a2e2e',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  reportBadgeText: { color: '#ffd9d9', fontSize: 11, fontWeight: '700' },
  noFeatures: { color: appColors.text.secondary, fontSize: 13, fontStyle: 'italic' },
  row: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: appColors.border.strong,
    paddingVertical: 8,
    gap: 4,
  },
  rowTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowKind: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  sourceBadge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 },
  badgeOsm: { backgroundColor: '#2d4a6b' },
  badgeGps: { backgroundColor: '#2e5d3a' },
  badgeSatellite: { backgroundColor: '#5b4a7a' },
  badgeLantmateriet: { backgroundColor: '#2a5b5f' },
  sourceBadgeText: { color: appColors.text.primary, fontSize: 11, fontWeight: '700' },
  rowMeta: { color: appColors.text.secondary, fontSize: 12 },
  // Gold border, like the target panel in the hole view — the same "you just tapped
  // something" accent, so a returning admin recognises the pattern.
  detailsPanel: {
    backgroundColor: appColors.accent.goldSoft,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    padding: 12,
    gap: 6,
  },
  detailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  detailsTitle: { color: appColors.text.primary, fontSize: 16, fontWeight: '700' },
  detailsCloseText: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  detailsSourceBadge: { alignSelf: 'flex-start' },
  detailsMeta: { color: appColors.text.secondary, fontSize: 12 },
});
